<script setup lang="ts">
import { computed, ref } from 'vue';

import { useReviewHotkeys } from '../composables/useReviewHotkeys';
import { formatReviewedOn, reviewFreshness } from '../utils/reviewFreshness';
import {
  formatDetailValue,
  resolveExtraDetails,
  resolveLinks,
  resolveMembers,
  resolveMembersLabel,
} from '../utils/reviewedItem';
import type { ReviewItemAws, ReviewItemCoreMetadata, ReviewItemGitHub } from '../api';

const props = withDefaults(
  defineProps<{
    review: ReviewItemAws | ReviewItemGitHub | ReviewItemCoreMetadata;
    approving: boolean;
    /** Whether there's somewhere to skip to. Only the bulk review stack has one. */
    skippable?: boolean;
  }>(),
  { skippable: false }
);

const emit = defineEmits<{ approve: []; skip: [] }>();

const members = computed(() => resolveMembers(props.review));
const membersLabel = computed(() => resolveMembersLabel(props.review));
const links = computed(() => resolveLinks(props.review));
const canReview = computed(() => props.review.canReview);
const freshness = computed(() => reviewFreshness(props.review.reviewedOn, props.review.reviewType));
const extraDetails = computed(() => resolveExtraDetails(props.review));
// Open by default rather than making the reviewer expand it to see what's there.
const detailsOpen = ref(0);

// Hotkeys go quiet along with the buttons they stand in for. "m" only ever opens the first link;
// with several, the rest are mouse/tap-only.
useReviewHotkeys(
  () => canReview.value && emit('approve'),
  computed(() => (canReview.value ? links.value[0]?.link : undefined)),
  () => emit('skip'),
  computed(() => props.skippable && !props.approving)
);
</script>

<template>
  <div>
    <!-- Skippable views carry this on the Skip button's tooltip instead; without a Skip button
         there, this is the only place the reviewer would ever see why the actions are disabled. -->
    <v-alert
      v-if="!canReview && !skippable"
      type="info"
      variant="tonal"
      text="You're not permitted to review this item."
      class="mb-4"
    />
    <div class="d-flex align-center ga-4">
      <h1>{{ review.reviewedItem?.name }}</h1>
      <v-spacer />
      <v-btn
        v-if="skippable"
        variant="text"
        :disabled="approving"
        :title="!canReview ? 'You are not permitted to review this item.' : undefined"
        text="Skip (S)"
        @click="emit('skip')"
      />
      <v-btn
        v-for="(link, index) in links"
        :key="link.link"
        :href="link.link"
        :disabled="!canReview"
        target="_blank"
        rel="noreferrer"
        variant="tonal"
        :text="index === 0 ? `${link.name} (M)` : link.name"
      />
      <v-btn
        :loading="approving"
        :disabled="!canReview"
        color="primary"
        text="Approve review (A)"
        @click="emit('approve')"
      />
    </div>

    <p class="d-flex align-center ga-2 mt-2">
      <v-icon :icon="freshness.icon" size="x-small" :color="freshness.color" :title="freshness.label" />
      <span
        >{{ freshness.label }}: last reviewed by {{ review.reviewer.name }} ({{ review.reviewer.email }}) on
        {{ formatReviewedOn(review.reviewedOn) }}.</span
      >
    </p>

    <h2 class="mt-4">{{ membersLabel }}</h2>
    <v-list v-if="members.length" density="compact">
      <v-list-item
        v-for="member in members"
        :key="member.id"
        :title="member.displayName"
        :subtitle="member.department ? `${member.email} · ${member.department}` : member.email"
      />
    </v-list>
    <p v-else>No {{ membersLabel.toLowerCase() }}.</p>

    <v-expansion-panels v-if="extraDetails.length" v-model="detailsOpen" class="mt-4">
      <v-expansion-panel title="Details" bg-color="surface-variant">
        <v-expansion-panel-text>
          <dl class="details-list">
            <template v-for="[key, value] in extraDetails" :key="key">
              <dt>{{ key }}</dt>
              <dd>{{ formatDetailValue(value) }}</dd>
            </template>
          </dl>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<style scoped>
.details-list {
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: 0.5rem 1.5rem;
  margin: 0;
}

.details-list dt {
  font-family: monospace;
  color: var(--text-understated, inherit);
}

.details-list dd {
  margin: 0;
  word-break: break-word;
}
</style>
